import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Two-click sign-off: a date picker (defaults to today) then `onConfirm(date)`.
 * Shared by the Dashboard resolutions list and the Calendar docket so both
 * behave identically — completing an item means committing to *when*.
 */
export default function SignOffPopover({ item, onConfirm }) {
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!date) return;
    setSubmitting(true);
    try {
      await onConfirm(date);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border transition-colors ml-auto border-[#2a2a2a] text-[#888] hover:text-white hover:border-[#c89f45]/60"
          data-testid={`signoff-${item.id}`}
        >
          Sign off
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 bg-[#111] border-[#2a2a2a] text-[#eaeaea] p-4"
        data-testid={`signoff-popover-${item.id}`}
        align="end"
      >
        <p className="label-eyebrow mb-1">Signing off</p>
        <p className="text-xs text-[#888] mb-3">When did you actually complete this?</p>
        <input
          type="date"
          value={date}
          max={today}
          onChange={(e) => setDate(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-2 py-1.5 text-sm font-mono text-[#eaeaea] focus:outline-none focus:border-[#c89f45] mb-3"
          data-testid={`signoff-date-${item.id}`}
        />
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setOpen(false)}
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-[#2a2a2a] text-[#888] hover:text-white"
            data-testid={`signoff-cancel-${item.id}`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!date || submitting}
            className="text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded bg-[#c89f45] text-[#0a0a0a] hover:bg-[#d4ac52] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`signoff-confirm-${item.id}`}
          >
            {submitting ? "Signing…" : "Sign off"}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
